// ── Athena Smart Presentation — reading config + inline highlight engine ──────
import React from 'react';

// ── Document tag parser — [DOC:título|data|resumo1;resumo2;resumo3] ───────────
// The backend includes this tag at the start of long structured responses.
// Returns { docHeader: {title,date,summaryItems} | null, cleanText }
export function parseDocumentTag(text) {
  if (!text) return { docHeader: null, cleanText: text };

  const re = /\[DOC:([^\]|]+?)(?:\|([^\]|]*?))?(?:\|([^\]]*?))?\]\n?/;
  const m = text.match(re);
  if (!m) return { docHeader: null, cleanText: text };

  const title       = (m[1] || '').trim();
  const date        = (m[2] || '').trim();
  const summaryRaw  = (m[3] || '').trim();
  const summaryItems = summaryRaw
    ? summaryRaw.split(';').map(s => s.trim()).filter(Boolean).slice(0, 5)
    : [];

  if (!title) return { docHeader: null, cleanText: text };

  return {
    docHeader: { title, date, summaryItems },
    cleanText: text.replace(re, '').trim(),
  };
}

// ── Reading mode config by primary cognitive trait ────────────────────────────
// Correção tipográfica (conforto de leitura estilo ChatGPT):
// - Tamanho base do corpo é FIXO (16px desktop / 16px mobile) e escala apenas
//   por --athena-font-scale (slider do usuário, já existente em WorkspacePage) —
//   via calc() no CSS, sem precisar passar fontScale como prop por toda a árvore.
// - cognitiveProfile não altera mais o tamanho da fonte — só o RITMO de leitura
//   (line-height / espaço entre parágrafos), que é o que essa personalização
//   realmente representa. Isso preserva 100% a capacidade de personalização
//   existente, só para de confundir "perfil cognitivo" com "tamanho de fonte".
const RHYTHM_BY_TRAIT = {
  VISUAL:         { lineHeight: { desktop: 1.72, mobile: 1.62 }, paragraphSpacing: 16 },
  ESTRUTURADO:    { lineHeight: { desktop: 1.52, mobile: 1.45 }, paragraphSpacing: 12 },
  CONVERSACIONAL: { lineHeight: { desktop: 1.65, mobile: 1.55 }, paragraphSpacing: 14 },
  'PRÁTICO':      { lineHeight: { desktop: 1.52, mobile: 1.45 }, paragraphSpacing: 12 },
};
// Default (sem perfil ativo) = referência ChatGPT: 26px/16px desktop, 24px/16px mobile.
const RHYTHM_DEFAULT = { lineHeight: { desktop: 1.625, mobile: 1.5 }, paragraphSpacing: 14 };

export function getReadingConfig(cognitiveProfile, isMobile = false) {
  const primary = (cognitiveProfile?.traits || [])[0];
  const rhythm = RHYTHM_BY_TRAIT[primary] || RHYTHM_DEFAULT;
  return {
    // calc() herda --athena-font-scale do ancestral (definido em WorkspacePage) —
    // nunca abaixo de 16px na base; a personalização do usuário multiplica a partir daí.
    fontSize: 'calc(16px * var(--athena-font-scale, 1))',
    lineHeight: isMobile ? rhythm.lineHeight.mobile : rhythm.lineHeight.desktop,
    paragraphSpacing: rhythm.paragraphSpacing,
  };
}

// ── Highlight engine — marks monetary values, dates, percentages, key labels ──
const HIGHLIGHT_RE = new RegExp(
  '(R\\$\\s*[\\d.,]+' +
  '|\\b\\d+[.,]?\\d*\\s*%' +
  '|\\b\\d{1,2}\\/\\d{1,2}(?:\\/\\d{2,4})?' +
  '|\\bem\\s+\\d+\\s+(?:dia|semana|m[eê]|hora|minuto)[a-záéíóúâêîôûãõ]*' +
  '|(?:Prazo|Valor|Data|Próximo\\s+passo|Próxima\\s+ação|Resultado|Meta|Deadline|Entrega)\\s*:)',
  'gi'
);

function hlStyle(text) {
  if (/R\$/.test(text)) return { color: '#34d399', fontWeight: 600 };
  if (/%/.test(text))   return { color: '#60a5fa', fontWeight: 600 };
  if (/\d{1,2}\/\d|\bem\s+\d/i.test(text)) return { color: '#f59e0b', fontWeight: 500 };
  return { color: '#a78bfa', fontWeight: 600 };  // key labels
}

function processString(text, baseKey) {
  HIGHLIGHT_RE.lastIndex = 0;
  const parts = [];
  let last = 0, m, idx = 0;
  while ((m = HIGHLIGHT_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span key={`${baseKey}-hl-${idx++}`} style={hlStyle(m[0])}>{m[0]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 0 ? text : parts;
}

// Walks React children tree and applies highlights to string leaves
export function applyHighlights(children, baseKey = 'h') {
  if (!children) return children;
  if (typeof children === 'string') return processString(children, baseKey);
  if (Array.isArray(children)) {
    return children.flatMap((child, i) => {
      if (typeof child === 'string') {
        const result = processString(child, `${baseKey}-${i}`);
        return Array.isArray(result) ? result : [result];
      }
      if (React.isValidElement(child)) return [child];
      return [child];
    });
  }
  return children;
}
